// src/components/Settings/WebAuthnSetup.tsx
import React, { useState, useEffect } from 'react';
import { Fingerprint, Shield, CheckCircle, Trash2, Key } from 'lucide-react';
import toast from 'react-hot-toast';
import { startRegistration } from '@simplewebauthn/browser';

interface WebAuthnSetupProps {
  userId: string;
  username: string;
  decryptedDEK: CryptoKey | null;
}

export const WebAuthnSetup: React.FC<WebAuthnSetupProps> = ({ 
  userId, 
  username, 
  decryptedDEK 
}) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  // ✅ 1. 使用 State 管理金鑰狀態
  const [hasDeviceKey, setHasDeviceKey] = useState(false); 
  const [isLoading, setIsLoading] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  // ✅ 2. 抽離檢查邏輯，同步更新 hasDeviceKey State
  const checkRegistrationStatus = async () => {
    try {
      const response = await fetch(`/api/auth/webauthn-status?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setIsRegistered(data.registered || false);
      }
    } catch (error) {
      console.warn('檢查指紋狀態失敗:', error);
    } finally {
      // 檢查 localStorage 並更新 State
      const keyExists = !!localStorage.getItem(`device_key_${userId}`);
      setHasDeviceKey(keyExists);
    }
  };

  useEffect(() => {
    const checkSupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setIsSupported(available);
        } catch {
          setIsSupported(false);
        }
      }
    };
    checkSupport();
    checkRegistrationStatus();
  }, [userId]);

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const handleRegister = async () => {
    if (!decryptedDEK) {
      toast.error('請先登入並確保加密金鑰已載入');
      return;
    }

    setIsEnrolling(true);
    try {
      const optionsRes = await fetch(`/api/auth/webauthn-register?userId=${userId}`, {
        headers: { 'X-User-Id': userId }
      });
      
      if (!optionsRes.ok) {
        const err = await optionsRes.json();
        throw new Error(err.error || '獲取註冊選項失敗');
      }

      const options = await optionsRes.json();
      const attestation = await startRegistration(options);

      const verifyRes = await fetch('/api/auth/webauthn-register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({ attestation })
      });

      const data = await verifyRes.json();
      if (!verifyRes.ok) {
        throw new Error(data.error || '註冊失敗');
      }

      setShowPasswordDialog(true);

    } catch (err) {
      const msg = err instanceof Error ? err.message : '指紋註冊失敗';
      toast.error(msg);
      console.error('註冊錯誤:', err);
    } finally {
      setIsEnrolling(false);
    }
  };

  const handlePasswordConfirm = async () => {
    if (!passwordInput.trim()) {
      toast.error('請輸入密碼');
      return;
    }
    if (!username || username.trim().length === 0) {
      toast.error('使用者名稱遺失，請重新登入');
      return;
    }
    if (!decryptedDEK) {
      toast.error('加密金鑰遺失，請重新登入');
      return;
    }

    setIsLoading(true);

    try {
      const loginRes = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          password: passwordInput
        })
      });

      const loginData = await loginRes.json();
      if (!loginRes.ok) {
        throw new Error(loginData.error || '登入失敗');
      }

      const deviceKey = loginData.user.deviceKey;
      if (!deviceKey) {
        throw new Error('後端未回傳裝置金鑰');
      }

      // ✅ 寫入 localStorage 綁定 userId 的 Key
      const storageKey = `device_key_${userId}`;
      localStorage.setItem(storageKey, deviceKey);

      // 加密 DEK 邏輯
      const rawDEK = await crypto.subtle.exportKey('raw', decryptedDEK);
      const encoder = new TextEncoder();
      const deviceSalt = encoder.encode(username.trim());

      const baseKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(deviceKey),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );

      const derivedKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: deviceSalt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
      );

      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encryptedDEKBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        derivedKey,
        rawDEK
      );

      const combinedData = new Uint8Array(iv.length + encryptedDEKBuffer.byteLength);
      combinedData.set(iv, 0);
      combinedData.set(new Uint8Array(encryptedDEKBuffer), iv.length);
      const deviceEncryptedDataKey = arrayBufferToBase64(combinedData);

      const updateRes = await fetch('/api/auth/webauthn-update-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({
          device_encrypted_data_key: deviceEncryptedDataKey
        })
      });

      if (!updateRes.ok) {
        const updateData = await updateRes.json();
        throw new Error(updateData.error || '同步裝置加密金鑰失敗');
      }

      toast.success('✅ 指紋註冊成功，裝置加密金鑰已就緒！');
      setShowPasswordDialog(false);
      setPasswordInput('');
      setIsRegistered(true);
      
      // ✅ 3. 重點：寫入成功後手動更新 State，立即使 UI 變更！
      setHasDeviceKey(true);

    } catch (err) {
      toast.error(err instanceof Error ? err.message : '建立裝置金鑰失敗');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('確定要移除指紋登錄嗎？')) return;

    try {
      const response = await fetch('/api/auth/webauthn-register', {
        method: 'DELETE',
        headers: { 'X-User-Id': userId }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || '移除失敗');
      }

      await fetch('/api/auth/webauthn-update-key', {
        method: 'DELETE',
        headers: { 'X-User-Id': userId }
      });

      // ✅ 4. 刪除時同步移除與更新 State
      localStorage.removeItem(`device_key_${userId}`);
      setHasDeviceKey(false);
      setIsRegistered(false);

      toast.success('指紋登錄已移除');

    } catch (err) {
      toast.error(err instanceof Error ? err.message : '移除失敗');
    }
  };

  if (!isSupported) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
        <p>您的裝置不支援指紋登錄</p>
        <p className="text-sm mt-1">請使用 Windows Hello、Touch ID 或 Android 指紋</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full mb-3">
            <Fingerprint className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">指紋登錄</h3>
        </div>

        {isRegistered ? (
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">指紋已註冊</div>
                  {/* ✅ 5. 渲染時直接讀取 State */}
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {hasDeviceKey ? '✅ 裝置金鑰已就緒' : '⚠️ 尚未建立裝置金鑰'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleDelete}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 p-2 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900 dark:text-white">尚未註冊指紋</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  註冊後可使用指紋快速登錄
                </div>
              </div>
              <button
                onClick={handleRegister}
                disabled={isEnrolling}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Fingerprint className="w-4 h-4" />
                {isEnrolling ? '註冊中...' : '註冊指紋'}
              </button>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-400 dark:text-gray-500 text-center border-t border-gray-200 dark:border-gray-700 pt-4">
          <p>🔐 指紋數據儲存在您的裝置安全區</p>
          <p>📌 支援 Windows Hello、Touch ID、Android 指紋</p>
        </div>
      </div>

      {showPasswordDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <Key className="w-12 h-12 text-blue-500 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">🔐 建立裝置金鑰</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                請輸入您的密碼以建立裝置金鑰，之後即可使用指紋登錄
              </p>
            </div>

            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="請輸入密碼"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white mb-4"
              autoFocus
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handlePasswordConfirm();
                }
              }}
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPasswordInput('');
                  toast.info('指紋已註冊，但未建立裝置金鑰，請先使用密碼登入一次');
                }}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={isLoading}
              >
                略過
              </button>
              <button
                onClick={handlePasswordConfirm}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? '建立中...' : '確認'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};